import PageHeader from '@/components/page-header';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import ValidationErrors from '@/components/validation-errors';
import { admin_accounts_path, new_admin_account_path } from '@/rails/routes';
import type { Plan } from '@/types/platform';
import { Link, router, useForm } from '@inertiajs/react';
import type { FormEvent } from 'react';

interface TemplateOption {
  key: string;
  name: string;
  summary: string;
}

interface Props {
  plans: Plan[];
  templates: TemplateOption[];
  form: {
    name: string | null;
    planKey: string;
    appName: string | null;
    templateKey: string;
  };
  errors: string[];
}

interface ClientForm {
  name: string;
  planKey: string;
  appName: string;
  templateKey: string;
}

// A select cannot hold an empty value, so "no app" has a name here and is sent as blank.
const NO_APP = 'none';

const New = ({ plans, templates, form: initial, errors }: Props) => {
  const form = useForm<ClientForm>({
    name: initial.name ?? '',
    planKey: initial.planKey,
    appName: initial.appName ?? '',
    templateKey: initial.templateKey || NO_APP,
  });
  const withApp = form.data.templateKey !== NO_APP;
  const template = templates.find((candidate) => candidate.key === form.data.templateKey);

  const submit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    form.transform((data) => ({
      account: {
        name: data.name,
        plan_key: data.planKey,
        app_name: data.templateKey === NO_APP ? '' : data.appName,
        template_key: data.templateKey === NO_APP ? '' : data.templateKey,
      },
    }));
    form.post(admin_accounts_path(), {
      // A refused client is rendered at the address it was sent to, which is
      // not a page of its own. Put this form's address back so a refresh lands here.
      onError: () =>
        router.replace({
          url: new_admin_account_path(),
          preserveState: true,
          preserveScroll: true,
        }),
    });
  };

  return (
    <div className="max-w-2xl space-y-6">
      <PageHeader
        title="New client"
        description="Creates the client's company on the chosen plan with no checkout, puts you on it as owner, and launches the first app from the template."
      />

      <ValidationErrors errors={Array.isArray(errors) ? errors : []} />

      <Card>
        <CardContent>
          <form onSubmit={submit} className="space-y-5">
            <div className="space-y-2">
              <Label htmlFor="account-name">Company name</Label>
              <Input
                id="account-name"
                value={form.data.name}
                onChange={(event) => form.setData('name', event.target.value)}
                autoComplete="organization"
                required
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="account-plan">Plan</Label>
              <Select
                value={form.data.planKey}
                onValueChange={(value) => form.setData('planKey', value)}
              >
                <SelectTrigger id="account-plan" className="w-full sm:w-64">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {plans.map((plan) => (
                    <SelectItem key={plan.key} value={plan.key}>
                      {plan.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="text-muted-foreground text-sm">
                Attached with no checkout: a comped or separately invoiced client.
              </p>
            </div>

            <div className="space-y-2">
              <Label htmlFor="account-template">First app</Label>
              <Select
                value={form.data.templateKey}
                onValueChange={(value) => form.setData('templateKey', value)}
              >
                <SelectTrigger id="account-template" className="w-full sm:w-64">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value={NO_APP}>No app yet</SelectItem>
                  {templates.map((candidate) => (
                    <SelectItem key={candidate.key} value={candidate.key}>
                      {candidate.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="text-muted-foreground text-sm">
                {withApp
                  ? (template?.summary ?? '')
                  : 'The company is created with no app. One can be added later.'}
              </p>
            </div>

            {withApp ? (
              <div className="space-y-2">
                <Label htmlFor="account-app-name">App name</Label>
                <Input
                  id="account-app-name"
                  value={form.data.appName}
                  onChange={(event) => form.setData('appName', event.target.value)}
                  aria-describedby="account-app-name-note"
                />
                <p id="account-app-name-note" className="text-muted-foreground text-sm">
                  Optional. Left blank, the app takes the company&apos;s name.
                </p>
              </div>
            ) : null}

            <div className="flex flex-wrap gap-2">
              <Button type="submit" disabled={form.processing}>
                {form.processing ? 'Setting up…' : 'Create client'}
              </Button>
              <Button variant="outline" asChild>
                <Link href={admin_accounts_path()}>Cancel</Link>
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>
    </div>
  );
};

export default New;
